// src/services/broadcast/manager.ts

import { CHANNEL_NAME, FALLBACK_KEY } from "@src/config/constant";

type EventCallback = (payload?: any) => void;

class BroadcastManager {
  private static instance: BroadcastManager;
  private channel: BroadcastChannel | null = null;
  private callbacks: Map<string, EventCallback[]> = new Map();
  private fallbackTimer: number | null = null;

  private constructor() {}

  static getInstance(): BroadcastManager {
    if (!BroadcastManager.instance) {
      BroadcastManager.instance = new BroadcastManager();
    }
    return BroadcastManager.instance;
  }

  init() {
    if (typeof window === "undefined") return;

    // --- 使用 BroadcastChannel ---
    if ("BroadcastChannel" in (window as Window)) {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event) => {
        const { type, payload } = event.data || {};
        this.dispatch(type, payload);
      };
    } else {
      // --- fallback 使用 localStorage ---
      window.addEventListener("storage", (e) => {
        if (e.key === FALLBACK_KEY && e.newValue) {
          try {
            const { type, payload } = JSON.parse(e.newValue);
            this.dispatch(type, payload);
          } catch (err) {
            console.error("Fallback storage parse error", err);
          }
        }
      });
    }

    window.addEventListener("beforeunload", () => this.destroy());
  }

  destroy() {
    this.channel?.close();
    this.channel = null;
    this.callbacks.clear();
    if (this.fallbackTimer) {
      window.clearTimeout(this.fallbackTimer);
    }
  }

  emit(type: string, payload?: any) {
    if (this.channel) {
      this.channel.postMessage({ type, payload });
    } else {
      // fallback 方式：触发 localStorage 事件
      localStorage.setItem(FALLBACK_KEY, JSON.stringify({ type, payload, timestamp: Date.now() }));
      // 清理（避免持续触发）
      setTimeout(() => {
        localStorage.removeItem(FALLBACK_KEY);
      }, 0);
    }
  }

  on(type: string, callback: EventCallback) {
    if (!this.callbacks.has(type)) {
      this.callbacks.set(type, []);
    }
    this.callbacks.get(type)!.push(callback);
  }

  off(type: string, callback?: EventCallback) {
    if (!this.callbacks.has(type)) return;
    if (!callback) {
      this.callbacks.delete(type);
    } else {
      const arr = this.callbacks.get(type)!;
      this.callbacks.set(
        type,
        arr.filter((cb) => cb !== callback),
      );
    }
  }

  private dispatch(type: string, payload?: any) {
    const handlers = this.callbacks.get(type);
    if (!handlers) return;

    // 防抖处理
    if (this.fallbackTimer) {
      window.clearTimeout(this.fallbackTimer);
    }

    this.fallbackTimer = window.setTimeout(() => {
      handlers.forEach((cb) => cb(payload));
    }, 100);
  }
}

export const broadcastManager = BroadcastManager.getInstance();
